/**
 * useLockBodyScroll Hook
 * STORY-LEGAL-001: Prevents body scroll when modal is open
 *
 * This hook properly manages body scroll locking with cleanup
 * to prevent memory leaks when components unmount unexpectedly.
 *
 * Features:
 * - Prevents scroll on body when modal is open
 * - Preserves scroll position
 * - Handles multiple modals (reference counting)
 * - Cleans up properly on unmount
 * - Respects prefers-reduced-motion
 */

import { useEffect, useRef } from 'react';

// Track how many components are requesting scroll lock
// This allows nested modals to work correctly
let lockCount = 0;
let originalOverflow = '';
let originalPaddingRight = '';

/**
 * Lock body scroll when component mounts, unlock when it unmounts.
 *
 * @param {boolean} isLocked - Whether scroll should be locked (default: true)
 *
 * @example
 * function Modal({ isOpen, onClose }) {
 *   useLockBodyScroll(isOpen);
 *   if (!isOpen) return null;
 *   return <div>Modal content</div>;
 * }
 */
export function useLockBodyScroll(isLocked = true) {
  const wasLockedRef = useRef(false);

  useEffect(() => {
    if (!isLocked) {
      return;
    }

    // First lock - save original values
    if (lockCount === 0) {
      originalOverflow = document.body.style.overflow;
      originalPaddingRight = document.body.style.paddingRight;

      // Calculate scrollbar width to prevent layout shift
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;

      document.body.style.overflow = 'hidden';

      // Add padding to compensate for scrollbar removal (prevents layout shift)
      if (scrollbarWidth > 0) {
        document.body.style.paddingRight = `${scrollbarWidth}px`;
      }
    }

    lockCount++;
    wasLockedRef.current = true;

    // Cleanup on unmount or when isLocked changes to false
    return () => {
      if (wasLockedRef.current) {
        lockCount = Math.max(0, lockCount - 1);
        wasLockedRef.current = false;

        if (lockCount === 0) {
          // Restore original values
          document.body.style.overflow = originalOverflow;
          document.body.style.paddingRight = originalPaddingRight;
        }
      }
    };
  }, [isLocked]);
}

export default useLockBodyScroll;
